/* global document */
/* global IntersectionObserver */
/* global window */
import { Container } from 'react-bootstrap';
import React, { useContext, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import '../styles/Page.css';
import SEO from './SEO';
import ContactForm from './ContactForm';
import { LoadingContext } from './context/LoadingContext';
import usePrefetch from '../hooks/usePrefetch';
import NotFound from './NotFound';
import hasStoreName from '../helpers/hasStoreName';
import AppContext from './context/AppContext';

// Page fetching func.
const fetchPage = async (handle = '') => {
    const response = await fetch(new URL(`/api/pages/handle/${handle}`, process.env.APP_URL));
    const data = await response.json();
    return data?.page ?? null;
};

const Page = ({ }) => {
    const { appContext } = useContext(AppContext);
    // Loading state
    const {
        startLoading,
        finishLoading,
    } = useContext(LoadingContext);
    const { handle } = useParams();
    const {
        isLoaded: pageDataLoaded,
        data: pageData,
    } = usePrefetch({
        key: 'page',
        resourceKey: 'handle',
        resourceValue: handle,
        prefetchFn: async () => {
            await startLoading();
            const data = await fetchPage(handle);
            data.bodyHtml = data.bodyHtml.replace(
                /(?<=<img ).*src="([^"]+)"/g,
                'data-src="$1" class="lazy"'
            );
            await finishLoading();
            return data;
        }
    });
    /** Add store name to title if not exists */
    const title = useMemo(() => {
        if (!pageDataLoaded
            || !pageData
            || !Object.keys(pageData).length > 0)
            return '';
        let title = pageData.title;
        if (!hasStoreName(title, appContext.config.storeName)) {
            title = `${title} - ${appContext.config.storeName}`;
        }
        return title;
    }, [pageDataLoaded, pageData]);

    useEffect(() => {
        document.addEventListener('DOMContentLoaded', function () {
            let lazyloadImages;

            function lazyload () {
                if(lazyloadThrottleTimeout) {
                    clearTimeout(lazyloadThrottleTimeout);
                }

                lazyloadThrottleTimeout = setTimeout(function () {
                    var scrollTop = window.scrollY;
                    lazyloadImages.forEach(function(img) {
                        if(img.offsetTop < (window.innerHeight + scrollTop)) {
                            img.src = img.dataset.src;
                            img.classList.remove('lazy');
                        }
                    });
                    if(lazyloadImages.length == 0) {
                        document.removeEventListener('scroll', lazyload);
                        window.removeEventListener('resize', lazyload);
                        window.removeEventListener('orientationChange', lazyload);
                    }
                }, 20);
            }

            if ('IntersectionObserver' in window) {
                lazyloadImages = document.querySelectorAll('img[loading="lazy"]');
                var imageObserver = new IntersectionObserver(function(entries, observer) {
                    entries.forEach(function(entry) {
                        if (entry.isIntersecting) {
                            var image = entry.target;
                            image.src = image.dataset.src;
                            image.classList.remove('lazy');
                            imageObserver.unobserve(image);
                        }
                    });
                });

                lazyloadImages.forEach(function(image) {
                    imageObserver.observe(image);
                });
            } else {
                var lazyloadThrottleTimeout;
                lazyloadImages = document.querySelectorAll('img[loading="lazy"]');

                document.addEventListener('scroll', lazyload);
                window.addEventListener('resize', lazyload);
                window.addEventListener('orientationChange', lazyload);
            }
        });
    }, []);

    if (!pageDataLoaded)
        return <div
            className={'page--loader'}>
        </div>;
    if (pageDataLoaded === true && (!pageData || Object.keys(pageData ?? {}).length === 0))
        return (<NotFound />);
    return <Container
        fluid>
        <div
            className={'page--section'}>
            {pageDataLoaded
                ? Object.keys(pageData ?? {}).length > 0
                    ? <>
                        {pageData.seoHtml?.length > 0
                            ? <div
                                dangerouslySetInnerHTML={{ __html: pageData.seoHtml }}></div>
                            : <SEO
                                seoTitle={title}
                                seoType={'website'}
                                canonicalUrl={new URL(`/pages/${pageData.handle}`, process.env.APP_URL).toString()}
                            />
                        }
                        <div
                            className={'page-content--heading'}>
                            <h1
                                className={'page-content--title'}>{pageData.title}</h1>
                        </div>
                        <div
                            className={'page-content--body'}>
                            <div
                                dangerouslySetInnerHTML={{ __html: pageData.bodyHtml }}></div>
                            {handle === 'contact-us' && (
                                <ContactForm />
                            )}
                        </div>
                    </>
                    : <p>Page does not exist.</p>
                : <p>Fetching page...</p>
            }
        </div>
    </Container>;
};

Page.propTypes = {};

export default Page;